import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { setFamilyMemberId } from '../../../store/slices/authSlice'

/** Auth0 custom claim URI for family member id (must match Auth0 Rule). */
const FAMILY_MEMBER_ID_CLAIM = 'https://inflectionmedicine.com/familyMemberId'

/**
 * Reads the familyMemberId custom claim from the Auth0 ID token.
 *
 * Derived synchronously from `user` on every render (not read back from Redux) so
 * callers that also read `user` in the same render (e.g. SentryUserSync) never see a
 * stale id from a prior session for one render after logout or a fast account switch.
 * Also mirrors the value into Redux as a write-only cache for other consumers.
 */
export function useFamilyMemberId(): string | undefined {
  const { user } = useAuth0()
  const dispatch = useDispatch()

  const familyMemberId = useMemo(() => {
    if (!user) return undefined
    const raw = (user as Record<string, unknown>)[FAMILY_MEMBER_ID_CLAIM]
    const value = typeof raw === 'string' ? raw.trim() : raw != null ? String(raw).trim() : ''
    return value || undefined
  }, [user])

  useEffect(() => {
    dispatch(setFamilyMemberId(familyMemberId))
  }, [familyMemberId, dispatch])

  return familyMemberId
}
